import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Barcode, Plus, Printer, QrCode, Search, Wand2 } from "lucide-react";
import PriceLists from "@/components/PriceLists";
import { PortButtons } from "@/components/DataPorting";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCanEdit, useHasModule } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";

const blank = {
  name: "", item_code: "", scan_code: "", barcode: "",
  item_type: "GOODS", hsn_sac_code: "", unit_symbol: "NOS",
  uqc_code: "NOS-NUMBERS", sale_price: 0, purchase_price: 0, tax_rate_percent: 18,
  tax_treatment: "TAXABLE", sale_price_includes_tax: false,
  track_inventory: false, track_batches: false, low_stock_threshold: 0,
};

export default function Items() {
  const [tab, setTab] = useState("items");
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [hsnResults, setHsnResults] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const canEdit = useCanEdit();
  const hasInventory = useHasModule("inventory");
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [symbology, setSymbology] = useState("BARCODE");
  const [labelOpts, setLabelOpts] = useState({ copies: 1, price: false, scope: "filtered" });
  const [assigning, setAssigning] = useState(false);

  const load = () => api.get("/items", { params: { q: q || undefined } }).then(({ data }) => setRows(data));

  // The symbology the organization chose in Settings is the default here; the
  // dialog can override it for one print without changing the preference.
  useEffect(() => {
    api.get("/organization/preferences")
      .then(({ data }) => setSymbology(data.values?.item_label_symbology || "BARCODE"))
      .catch(() => {});
  }, []);

  /** PDFs are fetched by the browser, not axios, so they open in a tab. */
  const labelUrl = (params) => {
    const qs = new URLSearchParams({ symbology, ...params });
    return `${api.defaults.baseURL}/items/labels.pdf?${qs}`;
  };

  const printLabels = () => {
    const ids = labelOpts.scope === "filtered" && q
      ? rows.filter((r) => r.scan_code).map((r) => r.id).join(",")
      : "";
    if (labelOpts.scope === "filtered" && q && !ids) {
      toast.error("Nothing in this search has a code to print");
      return;
    }
    window.open(labelUrl({
      ids, copies: String(labelOpts.copies || 1), price: String(!!labelOpts.price),
    }), "_blank", "noopener");
    setLabelsOpen(false);
  };

  const assignCodes = async () => {
    setAssigning(true);
    try {
      const { data } = await api.post("/items/assign-codes");
      toast.success(data.assigned
        ? `Assigned codes to ${data.assigned} item${data.assigned === 1 ? "" : "s"}`
        : "Every item already has a code");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setAssigning(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    api.get("/masters/units").then(({ data }) => setUnits(data));
  }, []);

  const searchHsn = async (value) => {
    setForm((f) => ({ ...f, hsn_sac_code: value }));
    if (value.length >= 2) {
      const { data } = await api.get("/masters/hsn", { params: { q: value } });
      setHsnResults(data.slice(0, 5));
    } else setHsnResults([]);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        sale_price: Number(form.sale_price) || 0,
        purchase_price: Number(form.purchase_price) || 0,
        tax_rate_percent: Number(form.tax_rate_percent) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
      };
      if (editing) await api.patch(`/items/${editing.id}`, payload);
      else await api.post("/items", payload);
      toast.success(editing ? "Item updated" : "Item created");
      setOpen(false);
      setEditing(null);
      setForm(blank);
      setHsnResults([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Items</h1>
          <p className="text-sm text-slate-500 mt-1">{rows.length} products &amp; services with HSN/SAC and GST rate</p>
        </div>
        {tab === "items" && (
          <div className="flex flex-wrap items-center gap-2">
            <PortButtons datasetKey="items" importable />
            <Button variant="outline" data-testid="print-labels-btn" onClick={() => setLabelsOpen(true)}
              className="gap-1.5 rounded-full">
              <Printer size={15} /> Labels
            </Button>
            {canEdit && (
              <Button variant="outline" data-testid="assign-codes-btn" disabled={assigning}
                onClick={assignCodes} className="gap-1.5 rounded-full">
                <Wand2 size={15} /> {assigning ? "Assigning…" : "Assign codes"}
              </Button>
            )}
            {canEdit && (
              <Button data-testid="new-item-btn" onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
                className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
                <Plus size={16} /> New Item
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {[["items", "Items"], ["prices", "Price lists"]].map(([key, label]) => (
          <button key={key} data-testid={`items-tab-${key}`} onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              tab === key ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "prices" && <PriceLists />}

      {tab === "items" && (<>
      <div className="relative w-full sm:w-72">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="items-search" className="pl-8 h-9" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Name, code or HSN" />
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="items-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Item</th>
              <th className="px-3 py-2.5 text-left font-semibold">Code</th>
              <th className="px-3 py-2.5 text-left font-semibold">Scan code</th>
              <th className="px-3 py-2.5 text-left font-semibold">Type</th>
              <th className="px-3 py-2.5 text-left font-semibold">HSN/SAC</th>
              <th className="px-3 py-2.5 text-left font-semibold">Unit</th>
              <th className="px-3 py-2.5 text-right font-semibold">Sale price</th>
              <th className="px-3 py-2.5 text-right font-semibold">GST</th>
              {hasInventory && <th className="px-3 py-2.5 text-right font-semibold">Stock</th>}
              <th className="px-4 py-2.5 text-right font-semibold" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((it) => (
              <tr key={it.id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`item-row-${it.id}`}>
                <td className="px-4 py-2.5 font-medium">{it.name}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{it.item_code || "—"}</td>
                <td className="px-3 py-2.5">
                  {it.scan_code ? (
                    <a href={`${api.defaults.baseURL}/items/${it.id}/label.pdf`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`item-label-${it.id}`}
                      title="Print this label"
                      className="font-mono text-xs font-semibold tracking-wider text-slate-800 hover:text-blue-700">
                      {it.scan_code}
                    </a>
                  ) : <span className="text-slate-300">—</span>}
                  {it.barcode && (
                    <span className="block font-mono text-[10px] text-slate-400">{it.barcode}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs">{it.item_type === "GOODS" ? "Goods" : "Service"}</td>
                <td className="px-3 py-2.5">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px]">{it.hsn_sac_code}</span>
                </td>
                <td className="px-3 py-2.5 font-mono text-xs">{it.unit_symbol}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inr(it.sale_price)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{it.tax_rate_percent}%</td>
                {hasInventory && (
                  <td className="px-3 py-2.5 text-right font-mono text-xs">
                    {it.track_inventory ? (
                      <span className={Number(it.stock_on_hand) <= 0 ? "text-rose-600 font-semibold" : ""}>
                        {Number(it.stock_on_hand || 0)}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-2.5 text-right">
                  {canEdit && (
                    <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
                      data-testid={`item-edit-${it.id}`}
                      onClick={() => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); }}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={hasInventory ? 10 : 9} className="px-4 py-14 text-center text-sm text-slate-500">No items yet</td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No items yet" testid="items-cards">
        {rows.map((it) => (
          <DataCard
            key={it.id}
            testid={`item-card-${it.id}`}
            title={it.name}
            subtitle={`${it.item_type === "GOODS" ? "Goods" : "Service"}${it.item_code ? ` · ${it.item_code}` : ""}`}
            right={
              <div className="font-mono text-sm font-bold tabular-nums">{inr(it.sale_price)}</div>
            }
            rows={[
              ["Scan code", it.scan_code
                ? <span className="font-mono font-semibold tracking-wider">{it.scan_code}</span>
                : "—"],
              ["HSN / SAC", it.hsn_sac_code],
              ["GST", `${it.tax_rate_percent}%`],
              ["Unit", it.unit_symbol],
              hasInventory && it.track_inventory
                ? ["In stock", <span className={Number(it.stock_on_hand) <= 0 ? "text-rose-600 font-semibold" : ""}>
                    {Number(it.stock_on_hand || 0)}
                  </span>]
                : null,
            ]}
            actions={
              canEdit && (
                <button className="text-xs font-medium text-blue-700"
                  data-testid={`item-edit-m-${it.id}`}
                  onClick={() => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); }}>
                  Edit
                </button>
              )
            }
          />
        ))}
      </CardList>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Name</Label>
              <Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Item code</Label>
              <Input data-testid="item-code-input" className="font-mono" value={form.item_code || ""}
                onChange={(e) => setForm((f) => ({ ...f, item_code: e.target.value }))}
                placeholder="Your SKU, e.g. CHR-EXE" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Scan code</Label>
              <Input data-testid="item-scan-code-input" className="font-mono uppercase tracking-wider"
                maxLength={8} value={form.scan_code || ""}
                onChange={(e) => setForm((f) => ({ ...f, scan_code: e.target.value.toUpperCase() }))}
                placeholder={editing ? "" : "Generated for you"} />
              <p className="text-[11px] leading-snug text-slate-500">
                8 letters and digits, e.g. ABCD1234. Leave it empty and one is
                generated. This is what the printed label carries.
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Barcode on the product</Label>
              <Input data-testid="item-barcode-input" className="font-mono" value={form.barcode || ""}
                onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
                placeholder="8901030875021 — optional" />
              <p className="text-[11px] leading-snug text-slate-500">
                For packaged goods that already carry a barcode. Scanning it at
                billing finds this item, so nothing needs relabelling.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm((f) => ({ ...f, item_type: v }))}>
                <SelectTrigger data-testid="item-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GOODS">Goods (HSN)</SelectItem>
                  <SelectItem value="SERVICE">Service (SAC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">HSN / SAC code</Label>
              <Input data-testid="item-hsn-input" className="font-mono" value={form.hsn_sac_code}
                onChange={(e) => searchHsn(e.target.value)} placeholder="9401 or 998311" />
              {!!hsnResults.length && (
                <div className="rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="hsn-suggestions">
                  {hsnResults.map((h) => (
                    <button key={h.code} className="w-full text-left px-2 py-1.5 hover:bg-slate-50 transition-colors duration-150"
                      onClick={() => {
                        setForm((f) => ({ ...f, hsn_sac_code: h.code, tax_rate_percent: h.default_rate }));
                        setHsnResults([]);
                      }}>
                      <span className="font-mono text-xs font-semibold">{h.code}</span>
                      <span className="text-[11px] text-slate-500 ml-2">{h.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Unit</Label>
              <Select value={form.unit_symbol}
                onValueChange={(v) => {
                  const u = units.find((x) => x.symbol === v);
                  setForm((f) => ({ ...f, unit_symbol: v, uqc_code: u?.uqc_code || "OTH-OTHERS" }));
                }}>
                <SelectTrigger data-testid="item-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => <SelectItem key={u.symbol} value={u.symbol}>{u.symbol} · {u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">GST rate</Label>
              <Select value={String(form.tax_rate_percent)} onValueChange={(v) => setForm((f) => ({ ...f, tax_rate_percent: Number(v) }))}>
                <SelectTrigger data-testid="item-tax-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sale price</Label>
              <Input data-testid="item-price-input" className="font-mono text-right" value={form.sale_price}
                onChange={(e) => setForm((f) => ({ ...f, sale_price: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Purchase price</Label>
              <Input data-testid="item-purchase-input" className="font-mono text-right" value={form.purchase_price}
                onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value }))} />
            </div>
            {hasInventory && (
              <div className="sm:col-span-2 space-y-3 rounded-md border border-slate-200 p-3">
                <label className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    <span className="font-medium">Track inventory</span>
                    <span className="block text-[11px] text-slate-500">
                      Issuing an invoice reduces this item&rsquo;s stock; a credit note restores it.
                    </span>
                  </span>
                  <Switch data-testid="item-track-inventory" checked={!!form.track_inventory}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, track_inventory: v }))} />
                </label>
                {form.track_inventory && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Reorder alert below</Label>
                      <Input data-testid="item-low-stock-input" className="font-mono text-right w-32"
                        value={form.low_stock_threshold}
                        onChange={(e) => setForm((f) => ({ ...f, low_stock_threshold: e.target.value }))} />
                    </div>
                    <label className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 text-sm">
                      <span>
                        <span className="font-medium">Track batches &amp; expiry</span>
                        <span className="block text-[11px] text-slate-500">
                          Purchase receipts will require a batch number for this item.
                        </span>
                      </span>
                      <Switch data-testid="item-track-batches" checked={!!form.track_batches}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, track_batches: v }))} />
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="item-cancel">Cancel</Button>
            <Button onClick={save} data-testid="item-save" className="bg-blue-600 hover:bg-blue-700">Save item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={labelsOpen} onOpenChange={setLabelsOpen}>
        <DialogContent className="max-w-md" data-testid="labels-dialog">
          <DialogHeader><DialogTitle>Print item labels</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Symbol</Label>
              <Select value={symbology} onValueChange={setSymbology}>
                <SelectTrigger data-testid="label-symbology-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BARCODE">
                    <span className="flex items-center gap-2"><Barcode size={14} /> Barcode (Code 128)</span>
                  </SelectItem>
                  <SelectItem value="QR">
                    <span className="flex items-center gap-2"><QrCode size={14} /> QR code</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] leading-snug text-slate-500">
                Both carry the same code, so switching does not invalidate
                labels already printed. Settings holds your default.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Which items</Label>
              <Select value={labelOpts.scope}
                onValueChange={(v) => setLabelOpts((o) => ({ ...o, scope: v }))}>
                <SelectTrigger data-testid="label-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="filtered">
                    {q ? `These ${rows.length} search results` : "All items with a code"}
                  </SelectItem>
                  <SelectItem value="all">All items with a code</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Copies of each</Label>
              <Input data-testid="label-copies-input" className="w-24 font-mono text-right"
                value={labelOpts.copies}
                onChange={(e) => setLabelOpts((o) => ({ ...o, copies: e.target.value }))} />
            </div>

            <label className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2.5 text-sm">
              <span>
                <span className="font-medium">Show the price</span>
                <span className="block text-[11px] text-slate-500">
                  Prints the sale price in the corner of each label.
                </span>
              </span>
              <Switch data-testid="label-price-switch" checked={!!labelOpts.price}
                onCheckedChange={(v) => setLabelOpts((o) => ({ ...o, price: v }))} />
            </label>

            <p className="text-[11px] leading-snug text-slate-500">
              A4 sheet, 3 &times; 8 at 63.5 &times; 33.9 mm — the common Avery
              L7159 pitch, so plain paper works as well as label stock.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelsOpen(false)} data-testid="labels-cancel">Cancel</Button>
            <Button onClick={printLabels} data-testid="labels-print" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Printer size={15} /> Open PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
